<template>
  <slot v-bind="{ displayItems, layoutPatterns, isVideoItem, isArticleItem, getCommonCardProps }" />
</template>

<script setup lang="ts">
import type { SegmentCollectionItem } from '~/types/article';

const props = defineProps({
  items: {
    type: Array as PropType<string[]>,
    required: true
  },
  rowlayout: {
    type: String,
    required: false,
    default: 'BBB'
  },
  repeat: {
    type: Number,
    required: false,
    default: 1
  },
  imageHide: {
    type: Boolean,
    required: false,
    default: false
  },
  cid: {
    type: String,
    required: true
  },
  removeUsed: {
    type: Boolean,
    required: false,
    default: true
  }
});

const {
  public: { lifespanExpired, articleTeaser }
} = useRuntimeConfig();
const teaserApi = (articleTeaser as { api: string; ttl: number }).api;

const globalSkipIds = inject<Ref<string[]>>('global_skip_ids', ref([]));
const emitCollection = (items: string[] = []) => {
  useBusEmitter(`DnCollection__collection__${props.cid}`, { items });
};

if (!props.removeUsed) {
  emitCollection();
}

const { activeLayouts, trimLayouts } = getRowLayout(props.rowlayout, props.repeat);
const { getCommonCardProps: _getCommonCardProps } = useCardProps();

const articlesPoolIds = props.removeUsed ? props.items.filter(id => !globalSkipIds.value.includes(id)) : props.items;
const requiredSlots = activeLayouts.reduce((sum, layout) => sum + layoutItemCount(layout), 0);
const fetchLimit = ref(Math.min(requiredSlots, articlesPoolIds.length));

const { replaceUrl } = useUrlReplace();
const { queries } = useTansQuery();

const itemQueries = queries(() =>
  articlesPoolIds.slice(0, fetchLimit.value).map(item => ({
    api: ref(replaceUrl(teaserApi, { id: item, embed_edition_content: 'true' })),
    options: { provider: 'articleTeaser', id: item },
    transform: transformTeaser
  }))
);

const validItems = computed(() =>
  itemQueries.value
    .filter(q => !q.loading && !!q.data)
    .map(q => q.data as SegmentCollectionItem)
    .filter(item => item.editionContent?.lifeSpan !== lifespanExpired.toString())
);

const allSettled = computed(() =>
  itemQueries.value.length > 0
    ? itemQueries.value.every(q => !q.loading)
    : props.items.length > 0 && articlesPoolIds.length === 0
);
const displayItems = computed(() => validItems.value.slice(0, requiredSlots));
const layoutPatterns = computed(() => trimLayouts(validItems.value));

watch(allSettled, (settled) => {
  if (!settled) { return; }

  const shortfall = requiredSlots - validItems.value.length;

  if (shortfall > 0 && fetchLimit.value < articlesPoolIds.length) {
    fetchLimit.value = Math.min(fetchLimit.value + shortfall, articlesPoolIds.length);
    return;
  }

  const survivingIds = displayItems.value.map(item => item.id).filter(Boolean);
  globalSkipIds.value.push(...survivingIds);
  emitCollection(survivingIds);
}, { immediate: true });

const isVideoItem = (item: unknown): item is SegmentCollectionItem & { type: 'video' } => (item as SegmentCollectionItem)?.type === 'video';

const isArticleItem = (item: unknown): item is SegmentCollectionItem & { type: 'news' | 'featureArticle' } =>
  (item as SegmentCollectionItem)?.type === 'news' || (item as SegmentCollectionItem)?.type === 'featureArticle';

const isSpecialLayout = activeLayouts.includes('bbbabbb');

const imageVisibility = (itemIndex: number): boolean => {
  return (isSpecialLayout && itemIndex >= 3) || props.imageHide;
};

const getCommonCardProps = (item: SegmentCollectionItem, itemIndex: unknown) => _getCommonCardProps(item, imageVisibility(typeof itemIndex === 'number' ? itemIndex : Number(itemIndex) || 0));
</script>
